import { BaseWindow } from './BaseWindow.js';

export class StatsWindow extends BaseWindow {
  constructor() {
    super({
      id: 'stats',
      title: 'Stats/Debug',
      name: 'Stats/Debug',
      width: 350,
      height: 'auto',  // Auto-size to content
      minWidth: 300,
      minHeight: 'auto',  // No minimum height restriction
      titleBarClass: 'stats-title-bar'
    });
    
    // References for external updates
    this.soldierCountEl = null;
    this.rendererTypeEl = null;
    this.gameTimerEl = null;
    this.teamStatsEl = null;
    this.debugPanel = null;
    this.debugStatsEl = null;
    this.mainStatsEl = null;
    this.helpTextEl = null;
    this.helpToggleContainer = null;
    
    // Event handlers bound to this instance
    this.handleDebugToggle = this.handleDebugToggle.bind(this);
    this.handleHelpToggle = this.handleHelpToggle.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
  }
  
  // Override storage key for backward compatibility
  getStorageKey(item) {
    if (item === 'visible') {
      return 'soulforge_statsVisible';
    }
    return super.getStorageKey(item);
  }
  
  createTitleBar() {
    const titleBar = document.createElement('div');
    titleBar.className = this.titleBarClass;
    titleBar.innerHTML = `
      <span class="stats-title">${this.windowTitle}</span>
      <div class="stats-controls">
        <span id="gameTimerDisplay" class="game-timer">0:00</span>
        <button id="refreshBtn" class="stats-button refresh" title="Refresh app">↻</button>
        <button id="closeStatsBtn" class="stats-button close">×</button>
      </div>
    `;
    
    // Set up event handlers
    const closeBtn = titleBar.querySelector('#closeStatsBtn');
    closeBtn.addEventListener('click', () => this.hide());
    
    const refreshBtn = titleBar.querySelector('#refreshBtn');
    refreshBtn.addEventListener('click', this.handleRefresh);
    
    // Store reference to timer
    this.gameTimerEl = titleBar.querySelector('#gameTimerDisplay');
    
    return titleBar;
  }
  
  createContent() {
    const content = document.createElement('div');
    content.style.cssText = 'overflow-y: auto;';
    
    // Main stats section
    const mainStats = document.createElement('div');
    mainStats.id = 'mainStats';
    mainStats.innerHTML = `
      <table class="main-stats-table">
        <tr>
          <td>Active Soldiers:</td>
          <td><span id="soldierCount" class="stats-count">0</span></td>
        </tr>
        <tr>
          <td>Renderer:</td>
          <td><span id="rendererType" class="renderer-info">2D</span> <span class="renderer-hint">(Press R to toggle)</span></td>
        </tr>
      </table>
    `;
    content.appendChild(mainStats);
    this.mainStatsEl = mainStats;
    
    // Team stats section (will be populated by UIManager)
    const teamStats = document.createElement('div');
    teamStats.id = 'teamStats';
    teamStats.style.cssText = 'margin-top: 10px; font-size: 11px;';
    teamStats.innerHTML = '<strong>Team Stats:</strong><br>';
    content.appendChild(teamStats);
    this.teamStatsEl = teamStats;
    
    // Help text section
    const helpText = document.createElement('div');
    helpText.id = 'helpText';
    helpText.innerHTML = `
      <strong>Controls:</strong><br>
      • Click and drag to spawn soldiers<br>
      • Drag with your finger or mouse to paint units<br>
      <br>
      <strong>Gameplay:</strong><br>
      • Yellow dots = skill level<br>
      • Veterans get: faster fire, faster bullets, better aim<br>
      • Soldiers defend against attackers<br>
      • Max level: 15 (purple L15!)<br>
    `;
    
    // Load saved help text visibility
    const helpTextVisible = localStorage.getItem('soulforge_helpTextVisible');
    if (helpTextVisible !== null) {
      helpText.style.display = helpTextVisible === 'true' ? 'block' : 'none';
    }
    content.appendChild(helpText);
    this.helpTextEl = helpText;
    
    // Debug panel section
    const debugPanel = document.createElement('div');
    debugPanel.id = 'debugPanel';
    debugPanel.style.display = 'none';
    debugPanel.innerHTML = `
      <strong>Debug Mode Active</strong><br>
      Red lines = targeting<br>
      White X = predicted aim point<br>
      Purple OS:# = off-screen prediction count<br>
      Orange border = edge targeting issues<br>
      <div id="debugStats"></div>
    `;
    
    // Load saved debug state
    const debugModeEnabled = localStorage.getItem('soulforge_debugMode') === 'true';
    if (debugModeEnabled) {
      debugPanel.style.display = 'block';
    }
    content.appendChild(debugPanel);
    this.debugPanel = debugPanel;
    this.debugStatsEl = debugPanel.querySelector('#debugStats');
    
    // Toggle container
    const helpToggleContainer = document.createElement('div');
    helpToggleContainer.id = 'helpToggleContainer';
    helpToggleContainer.innerHTML = `
      <label>
        <span class="sci-fi-checkbox">
          <input id="debugModeToggle" type="checkbox" ${debugModeEnabled ? 'checked' : ''}>
          <span class="checkmark"></span>
        </span>
        Enable Debug Mode
      </label>
      <label>
        <span class="sci-fi-checkbox">
          <input id="helpTextToggle" type="checkbox" ${helpTextVisible !== 'false' ? 'checked' : ''}>
          <span class="checkmark"></span>
        </span>
        Show Help Text
      </label>
    `;
    content.appendChild(helpToggleContainer);
    this.helpToggleContainer = helpToggleContainer;
    
    // Set up toggle event handlers
    const debugToggle = helpToggleContainer.querySelector('#debugModeToggle');
    debugToggle.addEventListener('change', this.handleDebugToggle);
    
    const helpToggle = helpToggleContainer.querySelector('#helpTextToggle');
    helpToggle.addEventListener('change', this.handleHelpToggle);
    
    // Store references
    this.soldierCountEl = mainStats.querySelector('#soldierCount');
    this.rendererTypeEl = mainStats.querySelector('#rendererType');
    
    return content;
  }
  
  handleDebugToggle(e) {
    const isChecked = e.target.checked;
    
    // Dispatch event to notify the game
    window.dispatchEvent(new CustomEvent('debugModeToggle', { detail: isChecked }));
    localStorage.setItem('soulforge_debugMode', isChecked.toString());
    
    // Update debug panel visibility
    if (this.debugPanel) {
      this.debugPanel.style.display = isChecked ? 'block' : 'none';
      
      // Ensure window stays on screen after content change
      this.ensureWindowOnScreen();
    }
  }
  
  handleHelpToggle(e) {
    const isChecked = e.target.checked;
    
    if (this.helpTextEl) {
      this.helpTextEl.style.display = isChecked ? 'block' : 'none';
      localStorage.setItem('soulforge_helpTextVisible', isChecked.toString());
      
      // Ensure window stays on screen after content change
      this.ensureWindowOnScreen();
    }
  }
  
  handleRefresh() {
    // For PWAs, we need to reload bypassing cache
    window.location.reload(true);
  }
  
  // Override init to handle special initialization
  init() {
    super.init();
    
    // Apply boot animation if visible
    if (this.isVisible && this.container) {
      this.container.classList.add('alien-boot-up');
      
      // Boot sound effect
      const audioContext = window.audioManager?.audioContext;
      if (audioContext) {
        const now = audioContext.currentTime;
        
        // Power up sweep
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.setValueAtTime(50, now);
        osc.frequency.exponentialRampToValueAtTime(400, now + 0.3);
        
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.05, now + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        
        osc.start(now);
        osc.stop(now + 0.4);
      }
    }
  }
  
  // Override show/hide to handle animations
  show() {
    if (!this.container) {
      this.init();
    }
    
    // Remove any existing animation classes
    this.container.classList.remove('alien-shut-down');
    
    // Show and animate
    this.container.style.display = 'flex';
    this.container.classList.add('alien-boot-up');
    
    // Boot sound effect
    const audioContext = window.audioManager?.audioContext;
    if (audioContext) {
      const now = audioContext.currentTime;
      
      // Power up sweep
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.frequency.setValueAtTime(50, now);
      osc.frequency.exponentialRampToValueAtTime(400, now + 0.3);
      
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.05, now + 0.2);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      
      osc.start(now);
      osc.stop(now + 0.4);
    }
    
    this.isVisible = true;
    this.saveState('visible', true);
    
    // Ensure window is fully visible
    this.ensureWindowOnScreen();
  }
  
  hide() {
    if (!this.container) return;
    
    // Save position and size
    this.saveCurrentPosition();
    this.saveCurrentSize();
    
    // Animation
    this.container.classList.remove('alien-boot-up');
    this.container.classList.add('alien-shut-down');
    
    // Shut down sound
    const audioContext = window.audioManager?.audioContext;
    if (audioContext) {
      const now = audioContext.currentTime;
      
      // Power down sweep
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(50, now + 0.3);
      
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      
      osc.start(now);
      osc.stop(now + 0.4);
    }
    
    // Hide after animation
    setTimeout(() => {
      if (this.container) {
        this.container.style.display = 'none';
        this.container.classList.remove('alien-shut-down');
      }
    }, 500);
    
    this.isVisible = false;
    this.saveState('visible', false);
  }
  
  // Public methods for external updates
  updateSoldierCount(count, max = null) {
    if (!this.soldierCountEl) return;
    
    if (max !== null) {
      this.soldierCountEl.textContent = `${count}/${max}`;
      
      // Color code based on how close to limit
      if (count >= max) {
        this.soldierCountEl.style.color = '#f00';
      } else if (count >= max * 0.8) {
        this.soldierCountEl.style.color = '#ff0';
      } else {
        this.soldierCountEl.style.color = '#0f0';
      }
    } else {
      this.soldierCountEl.textContent = count;
      this.soldierCountEl.style.color = '#0f0';
    }
  }
  
  updateRendererType(type) {
    if (this.rendererTypeEl) {
      this.rendererTypeEl.textContent = type === 'threejs' ? '3D' : '2D';
    }
  }
  
  updateGameTimer(timeString) {
    if (this.gameTimerEl) {
      this.gameTimerEl.textContent = timeString;
    }
  }
  
  setDebugPanelVisibility(visible) {
    if (this.debugPanel) {
      this.debugPanel.style.display = visible ? 'block' : 'none';
      
      // Ensure window stays on screen after content change
      this.ensureWindowOnScreen();
    }
    
    // Update checkbox state if needed
    const debugToggle = this.container?.querySelector('#debugModeToggle');
    if (debugToggle && debugToggle.checked !== visible) {
      debugToggle.checked = visible;
    }
  }
  
  // Get DOM element references for external systems
  getElements() {
    return {
      statsEl: this.soldierCountEl,
      debugPanel: this.debugPanel,
      debugStats: this.debugStatsEl,
      teamStats: this.teamStatsEl,
      mainStats: this.mainStatsEl,
      container: this.container
    };
  }
}

// Create singleton instance
export const statsWindow = new StatsWindow();